import Vue from 'vue'
import Sentry from '@/services/sentry'

import App from './App.vue'
import router from './router'
import store from './store/'
import VueObserveVisibility from 'vue-observe-visibility'
import FeatureFlagsManager from '@pvhcorp/stitch-hub-vue-feature-flags-manager'
import Tracking from '@/plugins/Tracking'
import 'intersection-observer'

import '@/styles/main.scss'
import ElementUI from '@/plugins/ElementUI'
import RemoteSettings from '@/plugins/RemoteSettings'
import defaultSettings from '@/constants/defaultSettings'

import StitchDS from '@pvhcorp/stitch-hub-ds/dist/stitchDS.common.js'
import StitchAssetBrowser from '@/plugins/StitchAssetBrowser'

import api from '@/services/api'
import RenderFarmMasterAPI from '@/services/renderFarmMasterServerAPI'
import DesignHub from '@/services/designHub'
import Utils from '@/services/utils'

// Initialize early to catch all errors
Sentry.initialize()

Vue.use(ElementUI)
Vue.use(StitchDS)
Vue.use(VueObserveVisibility)
Vue.use(FeatureFlagsManager, {
  url: process.env.VUE_APP_FEATURE_FLAGS_JSON
})
Vue.use(RemoteSettings, {
  settingsUrl: process.env.VUE_APP_REMOTE_SETTINGS_JSON,
  defaults: defaultSettings
})
Vue.use(
  Tracking,
  {
    mixpanelId: process.env.VUE_APP_MIXPANEL_KEY
  },
  {
    isEnabled: true,
    isDebugEnabled: process.env.NODE_ENV !== 'production',
    router: router
  }
)

Vue.config.productionTip = process.env.NODE_ENV === 'production'

Vue.prototype.$boardsApiBaseUrl = api.getApiBaseUrl()

const designHubBaseUrl = DesignHub.getDesignHubBaseUrl()

Vue.use(StitchAssetBrowser, {
  assetTypes: {
    job: {
      apiBaseUrl: RenderFarmMasterAPI.getApiBaseUrl(),
      loginUrl: `${designHubBaseUrl}jobs`,
      menuItemLabel: 'My Renders'
    },
    style: {
      apiBaseUrl: DesignHub.getDesignHubApiBaseUrl(),
      loginUrl: designHubBaseUrl,
      menuItemLabel: 'Styles'
    }
  }
})

/**
 */
const supportsWebp = async () => {
  if (!self.createImageBitmap) {
    return false
  }

  const webpData =
    // eslint-disable-next-line no-secrets/no-secrets -- just some base64 webp test
    'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA='
  const blob = await fetch(webpData).then(r => r.blob())

  return createImageBitmap(blob).then(
    () => true,
    () => false
  )
}

;(async () => {
  Vue.prototype.$webpSupport = await supportsWebp()
})()

// Enable feature flags when running on local API
if (process.env.VUE_APP_API_ENVIRONMENT === 'local') {
  Vue.prototype.$featureFlagsManager.canAccessFeature = () => true
}

/**
 */
const redirectToError404 = () => {
  const pathError404 = '/error-404'

  if (window.location.pathname !== pathError404) {
    window.location.pathname = pathError404
  }
}

/**
 */
const redirectToLogin = () => {
  const apiBaseUrl = Vue.prototype.$boardsApiBaseUrl
  const identityClientId = process.env.VUE_APP_IDENTITY_CLIENT_ID
  let identityBaseUrl = process.env.VUE_APP_IDENTITY_BASE_URL

  if (identityClientId) {
    if (!identityBaseUrl) {
      identityBaseUrl = `https://${
        process.env.VUE_APP_IDENTITY_SUBDOMAIN
      }.${Utils.getAppDomain()}/`
    }

    const loginUrl = new URL(`${identityBaseUrl}o/authorize/`)

    loginUrl.searchParams.set('response_type', 'code')
    loginUrl.searchParams.set('client_id', identityClientId)
    loginUrl.searchParams.set('redirect_uri', `${apiBaseUrl}id_auth/login/`)
    loginUrl.searchParams.set('state', window.location.href)

    window.location.assign(loginUrl.href)
  } else {
    console.warn(
      'The location of the STITCH Identity Service could not be determined. Please set "VUE_APP_IDENTITY_BASE_URL" or "VUE_APP_IDENTITY_CLIENT_ID" and try again.'
    )
  }
}

/**
 * Runs before the app is initialized.
 */
const initializeErrorHandler = () => {
  api.setCallbacks({
    errorCallback: error => {
      if (error.response) {
        const response = error.response

        if (
          [401, 403].includes(response.status) &&
          error.request.responseURL.includes('/whoami')
        ) {
          redirectToLogin()
        } else if (response.status === 404) {
          redirectToError404()
        } else if (response.status === 500) {
          store.dispatch('setError', {
            message: 'Oops, something went wrong on our side'
          })
        } else {
          const responseData = response.data
          let messages

          if (Array.isArray(responseData)) {
            messages = responseData
          } else if (responseData.error) {
            messages = [responseData.error]
          } else {
            messages = Object.values(responseData).flat()
          }

          store.dispatch('setError', {
            message: messages.join(', '),
            body: error
          })
        }
      }
    }
  })
}

initializeErrorHandler()

store.dispatch('initializeApp', Vue).then(() => {
  new Vue({
    router,

    store,

    render: h => h(App)
  }).$mount('#app')
})
